import {
  IDemoRoiRequestDetails,
  formatDemoRoiRequestDateTime,
} from "../../roi-request-create/model/roi-request-create.storage";
import {
  IRoiRequestViewDetailsGroup,
  IRoiRequestViewDetailsSection,
  IRoiRequestViewDetailsRow,
  IRoiRequestViewSection,
  IRoiRequestViewTableSection,
} from "../../roi-request-view/model/roi-request-view";
import {
  RoiSelectorColumn,
  RoiSelectorRow,
} from "../../roi-request-create/model/roi-request-create.types";

interface IRoiRequestPdfPatientSummary {
  fullName: string;
  mrn: string;
  dateOfBirth: string;
  sex: string;
}

interface IRoiRequestPdfTocItem {
  id: string;
  label: string;
  pageNumber: number;
}

interface IRoiRequestPdfPageBase {
  sectionId: string;
  sectionLabel: string;
  pageIndexInSection: number;
  pageCountInSection: number;
  pageNumber: number;
}

export interface IRoiRequestPdfDetailsPage extends IRoiRequestPdfPageBase {
  type: "details";
  groups: IRoiRequestViewDetailsGroup[];
}

export interface IRoiRequestPdfTablePage extends IRoiRequestPdfPageBase {
  type: "table";
  columns: RoiSelectorColumn[];
  rows: RoiSelectorRow[];
}

export type IRoiRequestPdfPage = IRoiRequestPdfDetailsPage | IRoiRequestPdfTablePage;
type IRoiRequestPdfPageWithoutNumber = Omit<IRoiRequestPdfPage, "pageNumber">;

export interface IRoiRequestPdfModel {
  fileName: string;
  title: string;
  printedAt: string;
  patient: IRoiRequestPdfPatientSummary;
  tocItems: IRoiRequestPdfTocItem[];
  pages: IRoiRequestPdfPage[];
  totalPages: number;
}

const PDF_TITLE = "Health Information Management Release of Information";
const COVER_AND_TOC_PAGES_COUNT = 2;
const DETAILS_PAGE_CAPACITY = 26;
const TABLE_PAGE_CAPACITY = 32;
const FLOWSHEETS_PAGE_CAPACITY = 18;
const LONG_TEXT_MEDIUM_THRESHOLD = 260;
const LONG_TEXT_HIGH_THRESHOLD = 420;

const formatPatientName = (value: string) => {
  const parts = value
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    const firstToken = parts[0];
    const remainingTokens = parts.slice(1).join(" ");

    return `${firstToken}, ${remainingTokens}`.toUpperCase();
  }

  return value.toUpperCase();
};

const getDetailsValue = (
  groups: IRoiRequestViewDetailsGroup[],
  label: string,
): string | null => {
  for (const group of groups) {
    const row = group.rows.find((item) => item.label === label);

    if (row?.value) {
      return row.value;
    }
  }

  return null;
};

const getPatientSummary = ({
  requestDetails,
  viewSections,
}: {
  requestDetails: IDemoRoiRequestDetails;
  viewSections: IRoiRequestViewSection[];
}): IRoiRequestPdfPatientSummary => {
  const demographicsSection = viewSections.find(
    (section): section is IRoiRequestViewDetailsSection =>
      section.type === "details" && section.id === "demographics",
  );

  const fullName =
    getDetailsValue(demographicsSection?.groups ?? [], "Full Name") ??
    "Unknown Patient";
  const dateOfBirth =
    getDetailsValue(demographicsSection?.groups ?? [], "Date of Birth") ??
    "—";
  const sex = getDetailsValue(demographicsSection?.groups ?? [], "Sex") ?? "—";

  return {
    fullName: formatPatientName(fullName),
    mrn: requestDetails.mrn,
    dateOfBirth: dateOfBirth.split(" (")[0] ?? dateOfBirth,
    sex: sex.charAt(0).toUpperCase() || "—",
  };
};

const getDetailRowWeight = (row: IRoiRequestViewDetailsRow) => {
  const textLength = `${row.label} ${row.value}`.trim().length;

  if (textLength >= LONG_TEXT_HIGH_THRESHOLD) {
    return 2;
  }

  if (textLength >= LONG_TEXT_MEDIUM_THRESHOLD) {
    return 1.5;
  }

  return 1;
};

const getTableRowWeight = (row: RoiSelectorRow, columns: RoiSelectorColumn[]) => {
  const maxValueLength = columns.reduce((maxLength, column) => {
    const value = row[column.key];
    return Math.max(maxLength, String(value ?? "—").length);
  }, 0);

  if (maxValueLength >= LONG_TEXT_HIGH_THRESHOLD) {
    return 2;
  }

  if (maxValueLength >= LONG_TEXT_MEDIUM_THRESHOLD) {
    return 1.25;
  }

  return 1;
};

const paginateDetailsSection = (
  section: IRoiRequestViewDetailsSection,
): Omit<IRoiRequestPdfDetailsPage, "pageNumber">[] => {
  const pages: Array<Omit<IRoiRequestPdfDetailsPage, "pageNumber">> = [];
  let currentGroups: IRoiRequestViewDetailsGroup[] = [];
  let remainingCapacity = DETAILS_PAGE_CAPACITY;

  const pushPage = () => {
    if (currentGroups.length === 0) {
      return;
    }

    pages.push({
      type: "details",
      sectionId: section.id,
      sectionLabel: section.label,
      pageIndexInSection: 0,
      pageCountInSection: 0,
      groups: currentGroups,
    });

    currentGroups = [];
    remainingCapacity = DETAILS_PAGE_CAPACITY;
  };

  section.groups.forEach((group) => {
    if (group.rows.length === 0) {
      return;
    }

    let rowIndex = 0;
    let isFirstGroupChunk = true;

    while (rowIndex < group.rows.length) {
      const title = group.title
        ? isFirstGroupChunk
          ? group.title
          : `${group.title} (cont.)`
        : undefined;
      const titleWeight = title ? 1 : 0;

      if (remainingCapacity < titleWeight + 1) {
        pushPage();
      }

      const chunkRows: IRoiRequestViewDetailsRow[] = [];
      let usedCapacity = titleWeight;

      while (rowIndex < group.rows.length) {
        const row = group.rows[rowIndex];
        const rowWeight = getDetailRowWeight(row);

        if (
          chunkRows.length > 0 &&
          usedCapacity + rowWeight > DETAILS_PAGE_CAPACITY
        ) {
          break;
        }

        if (
          chunkRows.length === 0 &&
          usedCapacity + rowWeight > DETAILS_PAGE_CAPACITY &&
          currentGroups.length > 0
        ) {
          break;
        }

        chunkRows.push(row);
        rowIndex += 1;
        usedCapacity += rowWeight;

        if (usedCapacity >= DETAILS_PAGE_CAPACITY) {
          break;
        }
      }

      if (chunkRows.length === 0) {
        pushPage();
        continue;
      }

      currentGroups.push({
        title,
        rows: chunkRows,
      });
      remainingCapacity -= usedCapacity;

      if (rowIndex < group.rows.length) {
        pushPage();
      }

      isFirstGroupChunk = false;
    }
  });

  pushPage();

  const normalizedPages = pages
    .map((page) => ({
      ...page,
      groups: page.groups.filter((group) => group.rows.length > 0),
    }))
    .filter((page) => page.groups.length > 0);

  return normalizedPages.map((page, index) => ({
    ...page,
    pageIndexInSection: index + 1,
    pageCountInSection: normalizedPages.length,
  }));
};

const paginateTableSection = (
  section: IRoiRequestViewTableSection,
): Omit<IRoiRequestPdfTablePage, "pageNumber">[] => {
  const pages: Array<Omit<IRoiRequestPdfTablePage, "pageNumber">> = [];
  const pageCapacity =
    section.id === "flowsheets" ? FLOWSHEETS_PAGE_CAPACITY : TABLE_PAGE_CAPACITY;
  let currentRows: RoiSelectorRow[] = [];
  let usedCapacity = 0;

  const pushPage = () => {
    if (currentRows.length === 0) {
      return;
    }

    pages.push({
      type: "table",
      sectionId: section.id,
      sectionLabel: section.label,
      pageIndexInSection: 0,
      pageCountInSection: 0,
      columns: section.columns,
      rows: currentRows,
    });

    currentRows = [];
    usedCapacity = 0;
  };

  section.rows.forEach((row) => {
    const rowWeight = getTableRowWeight(row, section.columns);

    if (currentRows.length > 0 && usedCapacity + rowWeight > pageCapacity) {
      pushPage();
    }

    currentRows.push(row);
    usedCapacity += rowWeight;

    if (usedCapacity >= pageCapacity) {
      pushPage();
    }
  });

  pushPage();

  const normalizedPages = pages.filter((page) => page.rows.length > 0);

  return normalizedPages.map((page, index) => ({
    ...page,
    pageIndexInSection: index + 1,
    pageCountInSection: normalizedPages.length,
  }));
};

export const buildRoiRequestPdfModel = ({
  requestDetails,
  viewSections,
}: {
  requestDetails: IDemoRoiRequestDetails;
  viewSections: IRoiRequestViewSection[];
}): IRoiRequestPdfModel => {
  const patient = getPatientSummary({ requestDetails, viewSections });
  const paginatedContent = viewSections.reduce<IRoiRequestPdfPageWithoutNumber[]>(
    (allPages, section) => [
      ...allPages,
      ...(section.type === "details"
        ? paginateDetailsSection(section)
        : paginateTableSection(section)),
    ],
    [],
  );

  const pages = paginatedContent.map((page, index) => ({
    ...page,
    pageNumber: index + COVER_AND_TOC_PAGES_COUNT + 1,
  })) as IRoiRequestPdfPage[];

  const tocMap = new Map<string, IRoiRequestPdfTocItem>();

  pages.forEach((page) => {
    if (!tocMap.has(page.sectionId)) {
      tocMap.set(page.sectionId, {
        id: page.sectionId,
        label: page.sectionLabel,
        pageNumber: page.pageNumber,
      });
    }
  });

  return {
    fileName: `roi-request-${requestDetails.requestId}.pdf`,
    title: PDF_TITLE,
    printedAt: formatDemoRoiRequestDateTime(new Date().toISOString()),
    patient,
    tocItems: Array.from(tocMap.values()),
    pages,
    totalPages: pages.length + COVER_AND_TOC_PAGES_COUNT,
  };
};
